import { applicantSection, otherAttachments, proxyAttachments, replySection, representationSection } from "modules/ebiForms/common";
import { householdSection, otherAdultsAttachment, tenantDeclarationSections } from "modules/ebiForms/definitions/sharedHousing";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E2. Zamiana lokali komunalnych — art. 21 ust. 1 pkt 2 i art. 21b ustawy z 21.06.2001 r. o ochronie praw lokatorów
 * (warunki zamiany — uchwała rady gminy; deklaracja i oświadczenia z art. 21b, bo zamiana kończy się nową umową najmu),
 * przy zamianie wzajemnej — zgoda wynajmującego. Dwa warianty (wariant wniosku w BE): zamiana na inny lokal z zasobu gminy
 * („z urzędu”) i zamiana wzajemna (międzylokatorska). Brak wzoru ogólnopolskiego; układ jak wzory Lublina WSM-019-01
 * (https://bip.lublin.eu/download/gfx/bip/userfiles/_public/import/e-urzad/opisy-uslug/wydzial-spraw-mieszkaniowych/zamiany/zamiana-lokalu-z-urzedu/2025-10-01/wsm-019-01.pdf),
 * Gorlic (zał. 2 do zarządzenia 65/2021, https://bip.malopolska.pl/e,pobierz,get.html?id=2634765) i Świnoujścia
 * (https://bip.um.swinoujscie.pl/attachments/download/94357) — wszystkie pobrane 22.10.2026; pominięto części
 * wypełniane przez administrację budynku (warunki mieszkaniowe, zaległości) — potwierdzenie zarządcy jako załącznik.
 */
const MUNICIPAL: EbiCondition = { field: "rodzajZamiany", equals: "gmina" };
const MUTUAL: EbiCondition = { field: "rodzajZamiany", equals: "wzajemna" };
const MUTUAL_TBS: EbiCondition = { all: [MUTUAL, { field: "drugiLokalWlasciciel", equals: "tbs" }] };

const E2: IEbiFormDefinition = {
  code: "E2",
  version: 1,
  title: "Wniosek o zamianę lokalu mieszkalnego",
  category: EEbiFormCategory.Housing,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zamiana lokalu komunalnego na inny lokal z zasobu gminy albo zamiana wzajemna z najemcą innego lokalu.",
  keywords: ["zamiana mieszkania", "zamiana lokalu", "mieszkanie komunalne", "zamiana wzajemna", "najem"],
  organ: "Wójt / Burmistrz / Prezydent Miasta albo jednostka zarządzająca mieszkaniowym zasobem gminy (np. ZBiLK, ZGM, ZNK)",
  legalBasis:
    "art. 21 ust. 1 pkt 2 i art. 21b ustawy z dnia 21 czerwca 2001 r. o ochronie praw lokatorów, mieszkaniowym zasobie gminy i o zmianie Kodeksu cywilnego; uchwała rady gminy w sprawie zasad wynajmowania lokali (warunki zamiany); przy zamianie wzajemnej — zgoda wynajmującego (Kodeks cywilny)",
  request: "Wnoszę o zamianę zajmowanego lokalu mieszkalnego na zasadach wskazanych we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty. Pełnomocnictwo — 17 zł opłaty skarbowej.",
  variantField: "rodzajZamiany",
  variants: {
    gmina: { title: "Wniosek o zamianę lokalu mieszkalnego na inny lokal z zasobu gminy" },
    wzajemna: { title: "Wniosek o wyrażenie zgody na zamianę wzajemną lokali mieszkalnych" },
  },
  sections: [
    applicantSection("Wnioskodawca (najemca lokalu)"),
    representationSection(),
    {
      code: "zamiana",
      title: "Rodzaj zamiany",
      fields: [
        {
          code: "rodzajZamiany",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "gmina", label: "zamianę zajmowanego lokalu na inny lokal z mieszkaniowego zasobu gminy" },
            { value: "wzajemna", label: "zgodę na zamianę wzajemną (międzylokatorską) z najemcą innego lokalu" },
          ],
        },
        {
          code: "oczekiwanyLokal",
          type: "radio",
          label: "Zamiana lokalu",
          required: true,
          visibleWhen: MUNICIPAL,
          options: [
            { value: "wiekszy", label: "na lokal większy" },
            { value: "mniejszy", label: "na lokal mniejszy" },
            { value: "rownorzedny", label: "na lokal równorzędny" },
            { value: "socjalny", label: "lokalu socjalnego na inny lokal socjalny" },
          ],
        },
        {
          code: "oczekiwania",
          type: "textarea",
          label: "Oczekiwania co do lokalu",
          hint: "Np. dzielnica, piętro, liczba pokoi.",
          visibleWhen: MUNICIPAL,
          maxLength: 2000,
        },
        { code: "drugiNajemca", type: "text", label: "Imię i nazwisko najemcy drugiego lokalu", required: true, visibleWhen: MUTUAL, maxLength: 200 },
        { code: "drugiLokalAdres", type: "text", label: "Adres drugiego lokalu", required: true, visibleWhen: MUTUAL, maxLength: 300 },
        {
          code: "drugiLokalWlasciciel",
          type: "radio",
          label: "Drugi lokal należy do",
          required: true,
          visibleWhen: MUTUAL,
          options: [
            { value: "gmina", label: "mieszkaniowego zasobu gminy" },
            { value: "tbs", label: "towarzystwa budownictwa społecznego (TBS)" },
            { value: "inny", label: "innego właściciela (np. spółdzielni mieszkaniowej)" },
          ],
        },
        {
          code: "przyczyny",
          type: "checkboxes",
          label: "Złożenie wniosku uzasadniam",
          required: true,
          options: [
            { value: "zdrowotne", label: "stanem zdrowia" },
            { value: "materialne", label: "sytuacją materialną" },
            { value: "rodzinne", label: "sytuacją rodzinną" },
            { value: "warunki", label: "poprawą warunków mieszkaniowych" },
            { value: "inne", label: "inne" },
          ],
        },
        { code: "uzasadnienie", type: "textarea", label: "Uzasadnienie wniosku", required: true, maxLength: 5000 },
        {
          code: "rozliczenieLokalu",
          type: "declaration",
          label: "Oświadczam, że rozliczę się z dotychczas zajmowanego lokalu zgodnie z obowiązującymi przepisami.",
          required: true,
        },
      ],
    },
    householdSection({
      title: "Osoby ubiegające się wraz z najemcą o zamianę lokalu",
      membersLabel: "Osoby przeprowadzające się wspólnie z wnioskodawcą",
      adultsQuestion: true,
    }),
    ...tenantDeclarationSections(),
    replySection(),
  ],
  attachments: [
    {
      code: "tytulPrawny",
      label: "Dokument potwierdzający tytuł prawny do zajmowanego lokalu (umowa najmu, decyzja o przydziale albo wyrok)",
      required: true,
    },
    {
      code: "potwierdzenieZarzadcy",
      label: "Potwierdzenie zarządcy o warunkach mieszkaniowych i stanie rozliczeń (brak zaległości albo realizowana ugoda)",
      hint: "Nieobowiązkowo — wiele urzędów wymaga go na własnym druku (część wniosku wypełnia administracja budynku).",
    },
    {
      code: "zgodaDrugiejStrony",
      label: "Zgoda drugiej strony zamiany i wszystkich pełnoletnich osób zamieszkałych w obu lokalach",
      hint: "Skan oświadczeń podpisanych przez te osoby.",
      visibleWhen: MUTUAL,
      required: true,
      multiple: true,
    },
    { code: "zgodaWlasciciela", label: "Zgoda właściciela drugiego lokalu (TBS) na zamianę", visibleWhen: MUTUAL_TBS, required: true },
    {
      code: "dokumentyZdrowotne",
      label: "Orzeczenie o niepełnosprawności albo zaświadczenie lekarskie",
      hint: "Gdy zamiana jest uzasadniona stanem zdrowia.",
      visibleWhen: { field: "przyczyny", includes: "zdrowotne" },
      required: true,
    },
    {
      code: "protokolLokalu",
      label: "Protokół stanu technicznego i zdjęcia pomieszczeń zajmowanego lokalu",
      hint: "Nieobowiązkowo — niektóre urzędy wymagają ich do giełdy zamian.",
      multiple: true,
    },
    otherAdultsAttachment(),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E2;
